import { useState, useRef, useEffect, useCallback } from 'react';
import { CommunicationType } from '@bandido/shared/types';

export const useAudio = () => {
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [audioBlob, setAudioBlob] = useState<Blob | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentPlayTime, setCurrentPlayTime] = useState(0);
  const [audioFile, setAudioFile] = useState<File | null>(null);

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const playTimerRef = useRef<NodeJS.Timeout | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const generateSafeFileName = () => `recording-${Date.now()}`.replace(/[^\w-]/g, '');

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/mpeg' });
        setAudioBlob(audioBlob);

        if (audioRef.current) {
          URL.revokeObjectURL(audioRef.current.src);
        }

        const audioURL = URL.createObjectURL(audioBlob);
        const audio = new Audio(audioURL);
        audioRef.current = audio;

        const audioFile = new File([audioBlob], `${generateSafeFileName()}.mp3`, { type: 'audio/mpeg' });
        setAudioFile(audioFile);
      };

      mediaRecorder.start(1000);
      setIsRecording(true);
      setRecordingTime(0);

      timerRef.current = setInterval(() => {
        setRecordingTime((prevTime) => prevTime + 1);
      }, 1000);
    } catch (error) {
      console.error('Error al iniciar la grabación:', error);
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();

      mediaRecorderRef.current.stream.getTracks().forEach((track) => track.stop());

      if (timerRef.current) {
        clearInterval(timerRef.current);
      }

      setIsRecording(false);

      if (audioChunksRef.current.length === 0) {
        return null;
      }

      const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/mpeg' });
      setAudioBlob(audioBlob);

      if (audioRef.current) {
        URL.revokeObjectURL(audioRef.current.src);
      }

      const audioURL = URL.createObjectURL(audioBlob);
      const audio = new Audio(audioURL);
      audioRef.current = audio;

      const audioFile = new File([audioBlob], `${generateSafeFileName()}.mp3`, { type: 'audio/mpeg' });
      setAudioFile(audioFile);

      return audioFile;
    }

    return null;
  };

  const updateCurrentTime = useCallback(() => {
    if (audioRef.current) {
      setCurrentPlayTime(audioRef.current.currentTime);
    }
  }, []);

  const handlePlayAudio = () => {
    if (audioRef.current && !isPlaying) {
      if (playTimerRef.current) {
        clearInterval(playTimerRef.current);
        playTimerRef.current = null;
      }

      setIsPlaying(true);
      setCurrentPlayTime(audioRef.current.currentTime);

      audioRef.current.addEventListener('timeupdate', updateCurrentTime);

      audioRef.current.play().catch((error) => {
        console.error('Error al reproducir audio:', error);
        setIsPlaying(false);
      });

      playTimerRef.current = setInterval(() => {
        if (audioRef.current) {
          setCurrentPlayTime(audioRef.current.currentTime);
        }
      }, 100);

      audioRef.current.onended = () => {
        if (audioRef.current) {
          audioRef.current.removeEventListener('timeupdate', updateCurrentTime);

          if (audioRef.current.duration) {
            setCurrentPlayTime(audioRef.current.duration);
          }
        }

        setIsPlaying(false);

        if (playTimerRef.current) {
          clearInterval(playTimerRef.current);
          playTimerRef.current = null;
        }
      };
    }
  };

  const handlePauseAudio = () => {
    if (audioRef.current && isPlaying) {
      audioRef.current.pause();

      audioRef.current.removeEventListener('timeupdate', updateCurrentTime);

      setIsPlaying(false);

      if (playTimerRef.current) {
        clearInterval(playTimerRef.current);
        playTimerRef.current = null;
      }
    }
  };

  const handleDeleteAudio = () => {
    setAudioBlob(null);
    setAudioFile(null);
    setRecordingTime(0);
    setCurrentPlayTime(0);

    if (audioRef.current) {
      URL.revokeObjectURL(audioRef.current.src);
      audioRef.current = null;
    }
  };

  const handleDeleteFile = () => {
    setAudioFile(null);
    setCurrentPlayTime(0);

    if (audioRef.current) {
      URL.revokeObjectURL(audioRef.current.src);
      audioRef.current = null;
    }

    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const loadAudioFromUrl = (url: string, communicationType?: CommunicationType) => {
    if (!url) return;

    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause();
        audioRef.current.removeEventListener('timeupdate', updateCurrentTime);
        setIsPlaying(false);
      }
      URL.revokeObjectURL(audioRef.current.src);
    }

    if (playTimerRef.current) {
      clearInterval(playTimerRef.current);
      playTimerRef.current = null;
    }

    setCurrentPlayTime(0);

    if (communicationType === CommunicationType.AUDIO_RECORD) {
      setAudioFile(null);
      setAudioBlob({} as Blob);
    } else if (communicationType === CommunicationType.AUDIO_FILE) {
      setAudioBlob(null);
      setAudioFile({} as File);
    }

    const audio = new Audio();
    audio.src = url;
    audioRef.current = audio;

    audio.addEventListener('loadedmetadata', () => {});

    audio.addEventListener('error', (e) => {
      console.error('Error al cargar el audio:', e);
      if (communicationType === CommunicationType.AUDIO_RECORD) {
        setAudioBlob(null);
      } else if (communicationType === CommunicationType.AUDIO_FILE) {
        setAudioFile(null);
      }
    });

    audio.load();
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60)
      .toString()
      .padStart(2, '0');
    const secs = Math.floor(seconds % 60)
      .toString()
      .padStart(2, '0');
    return `${mins}:${secs}`;
  };

  useEffect(
    () => () => {
      if (mediaRecorderRef.current && isRecording) {
        mediaRecorderRef.current.stop();
        mediaRecorderRef.current.stream.getTracks().forEach((track) => track.stop());
      }

      if (audioRef.current && isPlaying) {
        audioRef.current.pause();

        audioRef.current.removeEventListener('timeupdate', updateCurrentTime);
        setIsPlaying(false);
      }

      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
      if (playTimerRef.current) {
        clearInterval(playTimerRef.current);
        playTimerRef.current = null;
      }

      if (audioRef.current) {
        URL.revokeObjectURL(audioRef.current.src);
      }
    },
    [isRecording, isPlaying, updateCurrentTime],
  );

  return {
    isRecording,
    recordingTime,
    audioBlob,
    isPlaying,
    currentPlayTime,
    audioFile,
    audioRef,
    fileInputRef,
    setAudioFile,
    setAudioBlob,
    startRecording,
    stopRecording,
    handlePlayAudio,
    handlePauseAudio,
    handleDeleteAudio,
    handleDeleteFile,
    loadAudioFromUrl,
    formatTime,
  };
};
